/**
 * Version: V0.0.3.0
 * Created: 2026-06-12
 * Updated: 2026-06-13
 * Description: Navigation bar component matching F:\\Platforms\\Projects\\DentalFlow\\dentiefy.com\\frontend layout.
 */

import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X, LogIn, LogOut } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import Logo from '../common/Logo';
import ThemeSwitcher from '../common/ThemeSwitcher';

export default function Navbar({ session = null, onLogout = () => {}, theme, setTheme }) {
  const { t, language } = useLanguage();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const getDashboardUrl = () => {
    if (!session || !session.user) return '/dashboard';
    
    const roleSubdomainMap = {
      OWNER:              'supports',
      MODERATOR:          'supports',
      ENGINEER:           'supports',
      BILLING_ADMIN:      'supports',
      SECURITY_ADMIN:     'supports',
      SALES_MGR:          'supports',
      SUPPORT_AGENT:      'supports',
      SUPPORT_MGR:        'supports',
      COMPLIANCE_MOD:     'supports',
      PATIENT:            'patients',
      CLINIC:             'clinics',
      LAB:                'labs',
      SUPPLIER:           'suppliers',
      REPAIR_SERVICE:     'repair-service',
      TRACKING:           'tracking',
      WASTE_MANAGEMENT:   'waste-management'
    };
    const subdomain = session.user.subdomain || roleSubdomainMap[session.user.role] || 'patients';
    
    const devDomain = process.env.REACT_APP_MAIN_DOMAIN_DEV || 'dentiefy.test';
    const prodDomain = process.env.REACT_APP_MAIN_DOMAIN_PROD || 'dentiefy.com';
    const isLocal = window.location.hostname.includes('localhost') || 
                    window.location.hostname.includes('127.0.0.1') || 
                    window.location.hostname.includes(devDomain);
    if (isLocal) {
      const port = window.location.port;
      const protocol = window.location.protocol;
      return `${protocol}//${subdomain}.${devDomain}${port ? `:${port}` : ''}`;
    }
    return `https://${subdomain}.${prodDomain}`;
  };

  const handleLinkClick = () => {
    window.scrollTo(0, 0);
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { to: "/features", label: "nav_features" },
    { to: "/pricing", label: "nav_pricing" },
    { to: "/downloads", label: "nav_apps" },
    { to: "/blog", label: "nav_blog" },
    { to: "/partners", label: "nav_partners" },
    { to: "/support", label: "nav_support" }
  ];

  const dir = language === 'ar' ? 'rtl' : 'ltr';

  return (
    <nav id="navbar" className="bg-white/80 backdrop-blur-md sticky top-0 z-50 shadow-sm dark:bg-brand-purple-darker/80 dark:border-b dark:border-slate-800 w-full transition-colors duration-300">
      <div className="container mx-auto px-6 py-3">
        <div className="flex justify-between items-center">
          <Link to="/" onClick={handleLinkClick}>
            <Logo color="text-brand-purple-dark dark:text-white" />
          </Link>
          
          {/* Desktop Menu */}
          <div className={`hidden lg:flex items-center ${dir === 'rtl' ? 'space-x-reverse space-x-8' : 'space-x-8'}`}>
            {navLinks.map(link => (
              <NavLink 
                key={link.label} 
                to={link.to} 
                onClick={handleLinkClick} 
                className={({ isActive }) => 
                  `font-sans text-sm transition-colors duration-200 ${
                    isActive 
                      ? 'text-violet-700 dark:text-violet-400 font-semibold' 
                      : 'text-slate-600 hover:text-violet-700 dark:text-slate-300 dark:hover:text-violet-400'
                  }`
                }
              >
                {t(link.label)}
              </NavLink>
            ))}
          </div>

          <div className={`hidden lg:flex items-center ${dir === 'rtl' ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
            <ThemeSwitcher theme={theme} setTheme={setTheme} />
            {session ? (
              <>
                <a href={getDashboardUrl()} className="text-slate-600 hover:text-violet-700 dark:text-slate-300 dark:hover:text-violet-400 font-sans text-sm">{t('nav_dashboard')}</a>
                <button onClick={onLogout} className="text-slate-600 hover:text-violet-700 dark:text-slate-300 dark:hover:text-violet-400 flex items-center font-sans text-sm">
                  <LogOut className={`w-5 h-5 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                  {t('nav_logout')}
                </button>
              </>
            ) : (
              <>
                <NavLink to="/login" onClick={handleLinkClick} className="text-slate-600 hover:text-violet-700 dark:text-slate-300 dark:hover:text-violet-400 font-sans text-sm">{t('nav_login')}</NavLink>
                <NavLink to="/signup" onClick={handleLinkClick} className="bg-gradient-to-r from-[#6D28D9] to-[#4D3C77] text-white font-semibold px-5 py-2 rounded-lg shadow-md hover:shadow-lg transition-shadow font-sans text-sm">{t('nav_signup')}</NavLink>
              </>
            )}
          </div>

          <div className={`lg:hidden flex items-center ${dir === 'rtl' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
            <ThemeSwitcher theme={theme} setTheme={setTheme} />
            {session ? (
              <a href={getDashboardUrl()} aria-label="Dashboard" className="text-slate-600 hover:text-violet-700 dark:text-slate-300 dark:hover:text-violet-400 p-2 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700">
                <LogIn className="w-6 h-6" />
              </a>
            ) : (
              <NavLink to="/login" onClick={handleLinkClick} aria-label="Login" className="text-slate-600 hover:text-violet-700 dark:text-slate-300 dark:hover:text-violet-400 p-2 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700">
                <LogIn className="w-6 h-6" />
              </NavLink>
            )}
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Open menu" className="p-2 rounded-full text-slate-800 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700">
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 space-y-2">
            {navLinks.map(link => (
              <NavLink 
                key={link.label} 
                to={link.to} 
                onClick={handleLinkClick} 
                className="block py-2 text-slate-600 hover:text-violet-700 rounded-md text-center bg-slate-100 dark:bg-brand-purple-dark dark:text-slate-200 dark:hover:bg-brand-purple font-sans text-sm"
              >
                {t(link.label)}
              </NavLink>
            ))}
            <hr className="dark:border-slate-700 pt-2"/>
            {session ? (
              <button onClick={() => { onLogout(); setIsMobileMenuOpen(false); }} className="block mt-2 bg-red-600 text-white font-semibold px-5 py-2 rounded-lg shadow-md hover:shadow-lg transition-shadow text-center w-full font-sans text-sm">{t('nav_logout')}</button>
            ) : (
              <NavLink to="/signup" onClick={handleLinkClick} className="block mt-2 bg-gradient-to-r from-[#6D28D9] to-[#4D3C77] text-white font-semibold px-5 py-2 rounded-lg shadow-md hover:shadow-lg transition-shadow text-center font-sans text-sm">{t('nav_signup')}</NavLink>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
